import {
  getDocs,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  where,
  getCountFromServer,
  query,
} from 'firebase/firestore'
import { constructQuery } from '@core/utils/utils'
import { uniqBy } from 'lodash'
import { RevisionEventType } from '@core/revisions/revisions'
import i18n from '@/libs/i18n'
import store from '@/store'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
import { allowed, FEATURES } from '@/auth'

export default {
  namespaced: true,
  state: {
    list: [],
    filteredList: [],
    shared: [],
    current: {},
    currentData: {},
    count: 0,
  },
  getters: {
    getAll: state => state.list.map(docRef => ({ ...docRef.data(), id: docRef.id })),
    getFilteredList: state => state.filteredList.map(docRef => ({ ...docRef.data(), id: docRef.id })),
    getShared: state => state.shared.map(docRef => ({ ...docRef.data(), id: docRef.id })).filter(el => !el.deletedAt),
    getCurrent: state => state.current,
    getCurrentData: state => state.currentData,
    getCount: state => state.count,
  },
  mutations: {
    UPDATE_LIST(state, payload) {
      const { docs, pagination } = payload

      state.list = (!pagination) ? docs : uniqBy([...state.list, ...docs], 'id')
    },
    UPDATE_FILTERED_LIST(state, payload) {
      state.filteredList = payload
    },
    UPDATE_SHARED(state, payload) {
      state.shared = uniqBy([...state.shared, ...payload], 'id')
    },
    SET_CURRENT(state, payload) {
      state.current = payload
      state.currentData = ({ ...payload.data(), id: payload.id })
    },
    SET_COUNT(state, payload) {
      state.count = payload
    },
  },
  actions: {
    /**
     * Fetches all.
     *
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchAll({ commit }, payload = {}) {
      return new Promise((resolve, reject) => {
        const { formFields } = tenantCtx()

        const { queryOptions = {}, pagination = false } = payload

        const q = constructQuery(formFields, 'name', {
          ...queryOptions,
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_LIST', { docs: querySnapshot.docs, pagination })
            resolve(querySnapshot.docs)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Fetches document count
     *
     * @param {Object} context - Vuex context
     *
     * @returns {Promise<number>} - Count of matching documents
     */
    fetchCount({ commit }) {
      return new Promise(async (resolve, reject) => {
        try {
          const { formFields } = tenantCtx()

          const q = query(
            formFields,
            where('deletedAt', '==', null),
          )

          const snapshot = await getCountFromServer(q)
          const { count } = snapshot.data()

          commit('SET_COUNT', count)
          resolve(count)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('form fields') }),
            variant: 'danger',
          })
          reject(error)
        }
      })
    },

    /**
     * Fetches all system level regexes.
     *
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchShared({ commit }, queryOptions) {
      return new Promise((resolve, reject) => {
        const { sharedFormFields } = tenantCtx()

        const q = constructQuery(sharedFormFields, 'name', queryOptions)

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_SHARED', querySnapshot.docs)
            resolve(querySnapshot.docs)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Fetches a document by the given ID.
     *
     * @param {Object} id The page ID.
     *
     * @returns {Promise} Retrieves all fields in the document as an Object. Returns undefined if the document doesn't exist.
     */
    fetchById({ }, id) {
      return new Promise((resolve, reject) => {
        const { formFields } = tenantCtx()
        const docRef = doc(formFields, id)

        getDoc(docRef)
          .then(docSnapshot => {
            if (!docSnapshot.exists()) {
              reject(new Error('404'))
            }

            if (docSnapshot.data().deletedAt) {
              reject(new Error('404'))
            }

            resolve(docSnapshot)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Form field') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a document by the current item ID.
     *
     * @param {Object}
     * @param {string} id The current page ID
     */
    fetchCurrent({ dispatch, commit }, id) {
      return new Promise((resolve, reject) => {
        dispatch('fetchById', id)
          .then(res => {
            commit('SET_CURRENT', res)
            resolve(res)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Fetches on search query
     *
     * @param {Object}
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchFiltered({ commit }, queryOptions) {
      return new Promise((resolve, reject) => {
        const { formFields, sharedFormFields } = tenantCtx()

        const qFormFields = constructQuery(formFields, 'createdAt', {
          ...queryOptions,
        })

        const qShared = constructQuery(sharedFormFields, 'name', {
          ...queryOptions,
        })

        Promise.all([getDocs(qFormFields), getDocs(qShared)])
          .then(([querySnapshotFormFields, querySnapshotShared]) => {
            const result = [...querySnapshotFormFields.docs, ...querySnapshotShared.docs]

            commit('UPDATE_FILTERED_LIST', result)
            resolve(result)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('form fields') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * resets the filter list
     *
     * @param {Object}
     *
     * @returns {Promise}
     */
    resetFiltered({ commit }) {
      return new Promise(resolve => {
        commit('UPDATE_FILTERED_LIST', [])
        resolve()
      })
    },

    /**
     * Updates a document by the given payload.
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    update({ state }, { value, previousValue }) {
      return new Promise((resolve, reject) => {
        updateDoc(state.current.ref, value)
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.UPDATE_FORM_FIELDS,
              id: value.id,
              newValue: value,
              previousValue,
            })
            store.dispatch('notify', { body: i18n.t('{title} has been updated', { title: 'Form Field' }) })
            resolve(true)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('Form field') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Creates a document by the given payload.
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    create({ }, payload) {
      return new Promise((resolve, reject) => {
        if (!allowed(FEATURES.FORM_FIELDS_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('form fields') }),
            variant: 'danger',
          })

          return
        }

        const { formFields } = tenantCtx()
        const docRef = doc(formFields)

        setDoc(docRef, { id: docRef.id, deletedAt: null, ...payload })
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.CREATE_FORM_FIELDS,
              id: docRef.id,
              newValue: payload,
              previousValue: {},
            })

            store.dispatch('notify', { title: i18n.t('Great!'), body: i18n.t('A new {title} has been created 🚀', { title: i18n.t('form field') }) })
            resolve(docRef)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong creating the {title}', i18n.t('Form Field')),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Soft deletes a document.
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    softDelete({ }, payload) {
      return new Promise((resolve, reject) => {
        const { formFields } = tenantCtx()
        const docRef = doc(formFields, payload.id)

        updateDoc(docRef, {
          deletedAt: payload.deletedAt,
        })
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.DELETE_FORM_FIELDS,
              id: payload.id,
              newValue: {},
              previousValue: payload,
            })
            resolve(true)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
  },
}
